import { FloatingFocusManager, FloatingPortal, useDismiss, useFloating, useId, useInteractions, useRole } from '@floating-ui/react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { ReactNode, useRef } from 'react'

import { ScrollFades, useScrollFade } from '../../hooks/useScrollFade'
import { AstraButton } from '../Inputs/AstraButton/AstraButton'
import { AstraHeading } from '../Typography/AstraHeading/AstraHeading'
import { AstraParagraph } from '../Typography/AstraParagraph/AstraParagraph'

export type AstraAlertDialogProps = {
    isOpen: boolean
    title: string
    description: ReactNode
    cancelButtonLabel: string
    confirmButtonLabel: string
    onCancel?: () => void
    onConfirm?: () => void
}

/**
 * Alert Dialog interrupts the user to confirm an action, usually a destructive or irreversible one, before it
 * happens. It is modal, focuses the cancel button first, and has exactly two outcomes. Use Dialog instead when
 * the content is a form or anything the user works inside.
 *
 * @summary for confirming a destructive or irreversible action
 */
export const AstraAlertDialog = ({ isOpen, title, description, cancelButtonLabel, confirmButtonLabel, onCancel, onConfirm }: AstraAlertDialogProps) => {
    const titleId = useId()
    const descriptionId = useId()
    const cancelButtonRef = useRef<HTMLButtonElement>(null)

    const { refs, context } = useFloating({
        open: isOpen,
        onOpenChange: open => {
            if (!open) onCancel?.()
        },
    })

    // Unlike AstraDialog, an alert never dismisses on an outside press: confirming or cancelling
    // is the only way out, so the choice cannot be made by accident.
    const dismiss = useDismiss(context, {
        outsidePress: false,
    })
    const role = useRole(context, { role: 'alertdialog' })
    const { getFloatingProps } = useInteractions([dismiss, role])

    // Movement is what causes vestibular discomfort, so reduced motion drops the scale and keeps
    // the fade, which still signals the dialog arriving. The overlay is opacity-only either way.
    const shouldReduceMotion = useReducedMotion()
    const closedScale = shouldReduceMotion ? {} : { scale: 0.95 }
    const openScale = shouldReduceMotion ? {} : { scale: 1 }

    const { setScrollElement, fade } = useScrollFade()

    return (
        <AnimatePresence>
            {isOpen && (
                <FloatingPortal>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0, transition: { ease: 'easeIn', duration: 0.15 } }}
                        transition={{ ease: [0.32, 0.72, 0, 1], duration: 0.3 }}
                        className="fixed inset-0 z-elevated bg-black/25"
                        onPointerDown={e => e.stopPropagation()}
                    />
                    <FloatingFocusManager context={context} modal initialFocus={cancelButtonRef}>
                        <motion.div
                            initial={{ opacity: shouldReduceMotion ? 0 : 0.5, ...closedScale }}
                            animate={{ opacity: 1, ...openScale }}
                            exit={{ opacity: 0, ...closedScale, transition: { ease: 'easeIn', duration: 0.15 } }}
                            transition={{ ease: [0.32, 0.72, 0, 1], duration: 0.3 }}
                            ref={refs.setFloating}
                            data-test="alert-dialog"
                            // Narrower than AstraDialog on purpose: an alert holds one short question
                            className="fixed inset-0 z-elevated m-auto flex h-fit max-h-screen w-fit min-w-[24rem] max-w-[25rem] flex-col outline-none"
                            {...getFloatingProps({
                                'aria-labelledby': titleId,
                                'aria-describedby': descriptionId,
                                onPointerDown: e => e.stopPropagation(),
                            })}
                        >
                            <div className="relative flex min-h-0 flex-col overflow-hidden rounded-xl border bg-surface-base shadow-dialog">
                                <div className="flex items-center gap-4 p-4 pb-3">
                                    {/* h2 is the dialog's main heading; h3 sizing keeps the title from dominating the body */}
                                    <AstraHeading as="h2" id={titleId} className="text-h3 font-medium leading-h3 tracking-h3">
                                        {title}
                                    </AstraHeading>
                                </div>
                                <div className="relative flex min-h-0 flex-col">
                                    <div ref={setScrollElement} id={descriptionId} className="min-h-0 overflow-y-auto p-4 pt-0">
                                        {typeof description === 'string' ? (
                                            <AstraParagraph weight="regular" variant="body">
                                                {description}
                                            </AstraParagraph>
                                        ) : (
                                            description
                                        )}
                                    </div>
                                    <ScrollFades fade={fade} name="alert-dialog" />
                                </div>
                                <div className="flex justify-end gap-2 border-t border-subtle p-4">
                                    <AstraButton ref={cancelButtonRef} onClick={onCancel} variant="secondary" size="medium" label={cancelButtonLabel} />
                                    <AstraButton
                                        onClick={onConfirm}
                                        variant="critical"
                                        size="medium"
                                        data-test="alert-dialog-confirm"
                                        label={confirmButtonLabel}
                                    />
                                </div>
                            </div>
                        </motion.div>
                    </FloatingFocusManager>
                </FloatingPortal>
            )}
        </AnimatePresence>
    )
}
